import { CloseTemplateViewConfig } from "@common/types/closeTemplateViewConfig";
import { isEqual } from "@common/helpers/comparison";
import { DEFAULT_FORM_1099_REVIEW_FILTERS } from "@common/helpers/form1099-review-filters";
import { UNCATEGORIZED_ACCOUNT_ROLES } from "@common/helpers/accounting/uncategorized-account";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import { FinancialAccountType } from "@common/types/financialAccount";
import { TransactionFilters } from "@common/schemas/transaction.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import {
    TransactionColumn,
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import {
    CounterpartyDirectoryColumn,
    CounterpartyDirectoryView,
    CounterpartyDirectoryViewSchema,
} from "@common/types/views/counterpartyDirectoryView";
import { ViewSchema, ViewType, type View } from "@common/types/views/views";
import {
    injectReviewColumns,
    stripInjectedReviewColumns,
} from "@common/helpers/inject-review-columns";
import {
    CLOSE_TEMPLATE_REVIEW_FIELDS,
    CloseTemplateReviewField,
} from "@common/types/closeTemplateReviewField";
import {
    createTransactionReviewViewConfig,
    createTransactionsViewConfig,
    createUncategorizedReviewViewConfig,
} from "@common/helpers/close-template-task-view-config";
import { createDefaultCounterpartyDirectoryView } from "@components/settings/Directory/counterpartyDirectoryViewConfig";
import {
    TRANSACTION_PRESET_UNCATEGORIZED,
    TRANSACTION_PRESET_WITHOUT_COUNTERPARTY,
    type TransactionPreset,
} from "./closeTemplateTasksDrawer.constants";
import {
    ensureForm1099Eligible1099ReviewColumn,
    FORM_1099_ELIGIBLE_1099_REVIEW_COLUMN_OPTIONS,
} from "./form1099ReviewColumn";

export { ensureForm1099Eligible1099ReviewColumn } from "./form1099ReviewColumn";

/**
 * The stored shape of `viewConfig` on a close-template task is a full
 * `TransactionView` (the existing schema), giving us column management,
 * filter state, and future-proof serialisation for free.
 *
 * Profit & loss close-template tasks use the same persisted `View` shape as
 * `WorkflowsService` when creating P&L activation tasks (`ViewType.PROFIT_LOSS`,
 * `columns: []`, period range in `filters`).
 */

/** Review fields that can be configured on a TRANSACTION_REVIEW template task. */
export type ReviewField = CloseTemplateReviewField;

/** Every review field we can read back from a stored viewConfig. */
const ALL_REVIEW_FIELDS: ReviewField[] = [...CLOSE_TEMPLATE_REVIEW_FIELDS];

/** Review fields offered in the "Fields to review" selector. */
export const SELECTABLE_REVIEW_FIELDS: ReviewField[] = [
    "category",
    "counterparty",
    "ledger",
    "accrual_ledger",
    "entity",
    "class",
    "note",
];

export const REVIEW_FIELD_LABEL: Record<ReviewField, string> = {
    category: "Category",
    counterparty: "Counterparty",
    ledger: "Cash ledger",
    accrual_ledger: "Accrual ledger",
    entity: "Entity",
    class: "Class",
    note: "Memo",
};

export const REVIEW_FIELD_TO_TRANSACTION_REVIEW_FIELD: Record<
    ReviewField,
    TransactionReviewField
> = {
    category: TransactionReviewField.CATEGORY,
    counterparty: TransactionReviewField.COUNTERPARTY,
    ledger: TransactionReviewField.CASH_LEDGER,
    accrual_ledger: TransactionReviewField.ACCRUAL_LEDGER,
    entity: TransactionReviewField.ENTITY,
    class: TransactionReviewField.CLASS,
    note: TransactionReviewField.NOTE,
};

/**
 * Reads the `reviewFields` array from a raw viewConfig blob.
 * Returns an empty array when absent or invalid.
 */
export function readReviewFieldsFromViewConfig(
    raw: Record<string, unknown> | null,
): ReviewField[] {
    if (!raw) {
        return [];
    }
    const v = raw.reviewFields;
    if (!Array.isArray(v)) {
        return [];
    }
    return v.filter((f): f is ReviewField =>
        (ALL_REVIEW_FIELDS as readonly string[]).includes(f),
    );
}

/**
 * Returns a copy of the raw viewConfig with both `reviewFields` and the stored
 * view columns updated atomically. Review columns already in the view keep
 * their pin, size, and order; only newly selected fields are appended, and
 * deselected review columns are removed. Base context columns
 * (DATE/DESCRIPTION/AMOUNT, and CATEGORY/ledger when they are not review
 * variants) are preserved. Any extra top-level fields (e.g. `stickyViewType`)
 * are preserved unchanged.
 */
export function withReviewFieldsAndColumns(
    raw: Record<string, unknown> | null,
    reviewFields: ReviewField[],
): Record<string, unknown> {
    const currentView = parseTransactionViewConfig(raw);
    const updatedView =
        reviewFields.length > 0
            ? injectReviewColumns(currentView, reviewFields)
            : stripInjectedReviewColumns(currentView);
    return {
        ...(raw ?? {}),
        ...serializeTransactionViewConfig(updatedView),
        reviewFields,
    };
}

/**
 * Migrates a stored TRANSACTION_REVIEW viewConfig for both bases so old
 * templates (persisted before review columns were stored in the view) get the
 * review-variant columns and REVIEW indicator injected. Modified-cash-ledger
 * templates additionally drop the accrual-ledger field, which is not available
 * for that basis.
 */
export function normalizeTransactionReviewViewConfigForBasis(
    raw: Parameters<typeof withReviewFieldsAndColumns>[0],
    basis: CloseTemplateBasis | null,
): ReturnType<typeof withReviewFieldsAndColumns> | null {
    if (!raw) {
        return null;
    }
    const existingFields = readReviewFieldsFromViewConfig(raw);
    const migratedFields =
        basis === CloseTemplateBasis.MODIFIED_CASH_LEDGER
            ? existingFields.filter((f) => f !== "accrual_ledger")
            : existingFields;
    return withReviewFieldsAndColumns(raw, migratedFields);
}

const DEFAULT_CLOSE_TEMPLATE_TASK_TRANSACTION_VIEW: TransactionView = {
    type: ViewType.TRANSACTION,
    columns: [
        { id: TransactionColumn.DATE },
        { id: TransactionColumn.DESCRIPTION },
        { id: TransactionColumn.CATEGORY },
        { id: TransactionColumn.AMOUNT },
    ],
    options: {
        bulkActions: false,
        selection: false,
    },
    filters: {},
};

/**
 * Pre-set filters for each transaction preset option.
 * These are applied on top of the default transaction view when the user
 * selects a preset from the task type selector.
 */
export const TRANSACTION_PRESET_FILTERS: Record<
    TransactionPreset,
    TransactionFilters
> = {
    [TRANSACTION_PRESET_WITHOUT_COUNTERPARTY]: {
        withEmptyOrUnverifiedCounterparty: true,
    },
    [TRANSACTION_PRESET_UNCATEGORIZED]: {
        cashGeneralLedgerRoles: [...UNCATEGORIZED_ACCOUNT_ROLES],
    },
};

/**
 * Creates the default view for a TRANSACTION_REVIEW template task.
 * Includes DATE, DESCRIPTION, CATEGORY, ledger (basis-aware), AMOUNT,
 * plus any review-variant columns for the given `reviewFields` appended
 * in order (unpinned, required in the ViewConfig so they cannot be hidden).
 */
export function createDefaultCloseTemplateTransactionReviewView(
    basis: CloseTemplateBasis | null,
    reviewFields: ReviewField[] = [],
) {
    const [firstReviewField, ...otherReviewFields] = reviewFields;
    if (firstReviewField == null) {
        return {
            ...createTransactionsViewConfig({ basis }),
            reviewFields,
        };
    }
    return createTransactionReviewViewConfig({
        basis,
        reviewFields: [firstReviewField, ...otherReviewFields],
    });
}

/**
 * Builds a viewConfig for a transaction preset by taking the default
 * transaction view (with basis-aware ledger column) and overriding its
 * filters with the preset-specific ones.
 */
export function createPresetTransactionViewConfig(
    filters: TransactionFilters,
    basis: CloseTemplateBasis | null = null,
): Record<string, unknown> {
    return createTransactionsViewConfig({ filters, basis });
}

export function createUncategorizedTransactionReviewViewConfig(): Record<
    string,
    unknown
> {
    return createUncategorizedReviewViewConfig();
}

/**
 * Parse a raw jsonb blob into a `TransactionView`.
 * Falls back to the default view if the blob is missing or invalid.
 */
export function parseTransactionViewConfig(
    raw: Record<string, unknown> | null,
): TransactionView {
    if (!raw) {
        return DEFAULT_CLOSE_TEMPLATE_TASK_TRANSACTION_VIEW;
    }
    const result = TransactionViewSchema.safeParse(raw);
    if (result.success) {
        return result.data;
    }
    return DEFAULT_CLOSE_TEMPLATE_TASK_TRANSACTION_VIEW;
}

export type TransactionsViewType = "sticky" | "dynamic";

export const DEFAULT_TRANSACTIONS_VIEW_TYPE: TransactionsViewType = "sticky";

/**
 * Reads the template-level `stickyViewType` from the raw stored viewConfig JSON.
 * This field is stored at the top level of the viewConfig object (outside the
 * `TransactionView` structure) and is used to control whether filters are
 * resolved to specific transactionIds at task creation (sticky) or applied
 * dynamically at query time (dynamic).
 */
export function readTransactionsViewTypeFromViewConfig(
    raw: Record<string, unknown> | null,
): TransactionsViewType {
    if (!raw) {
        return DEFAULT_TRANSACTIONS_VIEW_TYPE;
    }
    const v = raw.stickyViewType;
    if (v === "sticky" || v === "dynamic") {
        return v;
    }
    return DEFAULT_TRANSACTIONS_VIEW_TYPE;
}

/**
 * Returns a copy of the raw viewConfig with `stickyViewType` set.
 */
export function withTransactionsViewType(
    raw: Record<string, unknown> | null,
    viewType: TransactionsViewType,
): Record<string, unknown> {
    return { ...(raw ?? {}), stickyViewType: viewType };
}

/**
 * Serialise a `TransactionView` into a jsonb-safe Record for storage.
 */
export function serializeTransactionViewConfig(
    view: TransactionView,
): Record<string, unknown> {
    return view as unknown as Record<string, unknown>;
}

/** Matches `WorkflowsService` P&L activation task `view` payload. */
function defaultProfitLossCloseTemplateView(): View {
    return {
        type: ViewType.PROFIT_LOSS,
        columns: [],
        filters: {},
    };
}

/** Parses stored template JSON into a P&L `View`, or the default shell. */
export function parseProfitLossCloseTemplateViewConfig(
    raw: Record<string, unknown> | null,
): View {
    if (!raw) {
        return defaultProfitLossCloseTemplateView();
    }
    const parsed = ViewSchema.safeParse(raw);
    if (parsed.success && parsed.data.type === ViewType.PROFIT_LOSS) {
        return parsed.data;
    }
    return defaultProfitLossCloseTemplateView();
}

export function serializeProfitLossCloseTemplateViewConfig(
    view: View,
): Record<string, unknown> {
    return view as unknown as Record<string, unknown>;
}

/**
 * Reads the template-level `numberOfMonthsToShowBeforeCurrentClosePeriods` from
 * the raw stored viewConfig JSON. This field is stored at the top level of the
 * viewConfig object (outside the `View` structure) so it survives round-trips
 * without being stripped by `ViewSchema`.
 */
export function readProfitLossMonthsFromViewConfig(
    raw: Record<string, unknown> | null,
): number | null {
    if (!raw) {
        return null;
    }
    const v = raw.numberOfMonthsToShowBeforeCurrentClosePeriods;
    return typeof v === "number" ? v : null;
}

/**
 * Shared helper that serialises a close-template-task viewConfig including the
 * template-level `numberOfMonthsToShowBeforeCurrentClosePeriods` config field.
 *
 * Strips `periodStart` / `periodEnd` from `filters` — these are inferred at
 * apply-time from `startDate` / `endDate` and must not be persisted on the
 * template.
 */
function serializeCloseTemplateViewConfigWithMonths(
    view: View,
    serializeFn: (v: View) => Record<string, unknown>,
    numberOfMonthsToShowBeforeCurrentClosePeriods: number | null,
): Record<string, unknown> {
    const strippedFilters = { ...view.filters };
    delete strippedFilters.periodStart;
    delete strippedFilters.periodEnd;
    const strippedView: View = { ...view, filters: strippedFilters };
    const base = serializeFn(strippedView);
    if (numberOfMonthsToShowBeforeCurrentClosePeriods != null) {
        return {
            ...base,
            numberOfMonthsToShowBeforeCurrentClosePeriods,
        };
    }
    return base;
}

export function serializeProfitLossCloseTemplateViewConfigWithMonths(
    view: View,
    numberOfMonthsToShowBeforeCurrentClosePeriods: number | null,
): Record<string, unknown> {
    return serializeCloseTemplateViewConfigWithMonths(
        view,
        serializeProfitLossCloseTemplateViewConfig,
        numberOfMonthsToShowBeforeCurrentClosePeriods,
    );
}

// ---------------------------------------------------------------------------
// Balance Sheet
// ---------------------------------------------------------------------------

/** Matches the P&L pattern for Balance Sheet template tasks. */
export function defaultBalanceSheetCloseTemplateView(): View {
    return {
        type: ViewType.BALANCE_SHEET,
        columns: [],
        filters: {},
    };
}

/** Parses stored template JSON into a Balance Sheet `View`, or the default shell. */
export function parseBalanceSheetCloseTemplateViewConfig(
    raw: Record<string, unknown> | null,
): View {
    if (!raw) {
        return defaultBalanceSheetCloseTemplateView();
    }
    const parsed = ViewSchema.safeParse(raw);
    if (parsed.success && parsed.data.type === ViewType.BALANCE_SHEET) {
        return parsed.data;
    }
    return defaultBalanceSheetCloseTemplateView();
}

export function serializeBalanceSheetCloseTemplateViewConfig(
    view: View,
): Record<string, unknown> {
    // Intentionally delegates to the P&L serializer — the two share the same
    // wire format (a plain View object serialised to Record).
    return serializeProfitLossCloseTemplateViewConfig(view);
}

/**
 * Reads the template-level `numberOfMonthsToShowBeforeCurrentClosePeriods` from
 * the raw stored viewConfig JSON for a Balance Sheet task.
 * Shares the same field as the P&L config.
 */
export function readBalanceSheetMonthsFromViewConfig(
    raw: Record<string, unknown> | null,
): number | null {
    return readProfitLossMonthsFromViewConfig(raw);
}

export function serializeBalanceSheetCloseTemplateViewConfigWithMonths(
    view: View,
    numberOfMonthsToShowBeforeCurrentClosePeriods: number | null,
): Record<string, unknown> {
    return serializeCloseTemplateViewConfigWithMonths(
        view,
        serializeBalanceSheetCloseTemplateViewConfig,
        numberOfMonthsToShowBeforeCurrentClosePeriods,
    );
}

// ---------------------------------------------------------------------------
// Form 1099 (counterparty directory)
// ---------------------------------------------------------------------------

/**
 * Column order for the 1099 task table: Name, Accounts, Journal entries,
 * Amount, 1099 eligible (review variant).
 *
 * This intentionally differs from the general Directory default columns:
 * - "Created by" is dropped — it isn't relevant to reviewing 1099
 *   eligibility and just takes up space.
 * - "Debit"/"Credit" are replaced by a single "Amount" column — the 1099
 *   task always filters to Money Out, so Credit is always empty; showing
 *   both is confusing when only the debit amount is ever populated.
 * - ELIGIBLE_1099 uses the review variant so operators can mark the field
 *   reviewed without changing eligibility. The unreviewed overlay filter is
 *   applied at runtime in Form1099TaskBody (not stored on the template).
 */
function form1099TaskColumns(): CounterpartyDirectoryView["columns"] {
    return [
        {
            id: CounterpartyDirectoryColumn.NAME,
            options: { pin: "left" },
        },
        { id: CounterpartyDirectoryColumn.ACCOUNTS },
        { id: CounterpartyDirectoryColumn.JOURNAL_ENTRIES },
        { id: CounterpartyDirectoryColumn.AMOUNT },
        {
            id: CounterpartyDirectoryColumn.ELIGIBLE_1099,
            options: { ...FORM_1099_ELIGIBLE_1099_REVIEW_COLUMN_OPTIONS },
        },
    ];
}

export function defaultForm1099CloseTemplateView(): CounterpartyDirectoryView {
    return {
        ...createDefaultCounterpartyDirectoryView(),
        columns: form1099TaskColumns(),
        filters: { ...DEFAULT_FORM_1099_REVIEW_FILTERS },
    };
}

/**
 * Detects the pre-PRO-10848 1099 column layout, which reused the generic
 * Directory default (Name, Accounts, Created by, Journal entries, Debit,
 * Credit, 1099 eligible, Actions). Only column id + pin are compared so a
 * resized-but-otherwise-untouched view still counts as the legacy default.
 */
function hasLegacyForm1099Columns(
    columns: CounterpartyDirectoryView["columns"],
): boolean {
    const normalize = (cols: CounterpartyDirectoryView["columns"]) =>
        cols.map((col) => ({ id: col.id, pin: col.options?.pin ?? null }));
    return isEqual(
        normalize(columns),
        normalize(createDefaultCounterpartyDirectoryView().columns),
    );
}

/**
 * Upgrades a stored 1099 view that still carries the legacy default column
 * layout to the new 1099-specific columns (Name, Accounts, Journal entries,
 * Amount, 1099 eligible). Existing tasks/templates created before PRO-10848
 * persisted the generic Directory default, so without this they would keep
 * showing Created by/Debit/Credit until a manual reset. Views whose columns
 * were genuinely customised are returned unchanged so user choices survive.
 */
export function upgradeLegacyForm1099Columns(
    view: CounterpartyDirectoryView,
): CounterpartyDirectoryView {
    if (!hasLegacyForm1099Columns(view.columns)) {
        return ensureForm1099Eligible1099ReviewColumn(view);
    }
    return { ...view, columns: form1099TaskColumns() };
}

export function parseForm1099CloseTemplateViewConfig(
    raw: object | null,
): CounterpartyDirectoryView {
    if (!raw) {
        return defaultForm1099CloseTemplateView();
    }
    const parsed = CounterpartyDirectoryViewSchema.safeParse(raw);
    if (parsed.success) {
        return upgradeLegacyForm1099Columns(parsed.data);
    }
    return defaultForm1099CloseTemplateView();
}

export function serializeForm1099CloseTemplateViewConfig(
    view: CounterpartyDirectoryView,
): CloseTemplateViewConfig {
    // Strip apply-time-only fields so templates stay workspace/year agnostic.
    const strippedFilters = { ...view.filters };
    delete strippedFilters.periodStart;
    delete strippedFilters.periodEnd;
    delete strippedFilters.entityIds;
    return {
        ...view,
        filters: strippedFilters,
    };
}

// ---------------------------------------------------------------------------
// Shared financial account type utility
// ---------------------------------------------------------------------------

const FINANCIAL_ACCOUNT_TYPES: string[] = Object.values(FinancialAccountType);

export function isFinancialAccountType(
    value: string,
): value is FinancialAccountType {
    return FINANCIAL_ACCOUNT_TYPES.includes(value);
}

// ---------------------------------------------------------------------------
// Re-exports: Document Retrieval & Reconciliation
// ---------------------------------------------------------------------------

export {
    defaultDocumentRetrievalCloseTemplateView,
    parseDocumentRetrievalCloseTemplateViewConfig,
    serializeDocumentRetrievalCloseTemplateViewConfig,
    readDocumentRetrievalFinancialAccountType,
    readDocumentRetrievalFinancialAccountIds,
    readDocumentRetrievalStartDateOverride,
    readDocumentRetrievalEndDateOverride,
    serializeDocumentRetrievalCloseTemplateViewConfigWithDates,
} from "./documentRetrievalViewConfig";

export {
    readReconciliationFinancialAccountTypes,
    readReconciliationFinancialAccountIds,
    serializeReconciliationCloseTemplateViewConfig,
} from "./reconciliationViewConfig";

// ---------------------------------------------------------------------------
// Accrual review
// ---------------------------------------------------------------------------

/**
 * Builds and serialises a viewConfig for an accrual-review close-template task.
 *
 * `entityIds` and the date range are intentionally omitted: `entityIds` is
 * injected at apply time, and the close-period start/end are injected by
 * `buildViewConfig` in the backend (since no explicit `start`/`end` is present).
 */
export { createAccrualReviewViewConfig } from "@common/helpers/close-template-task-view-config";
